// Blog posts sourced from markdown files in this directory. Each file may carry
// YAML-ish frontmatter (title, author, created_at, updated_at, slug). slug
// defaults to the filename without the `.md` extension and must be unique.

export interface Post {
  slug: string;
  title: string;
  author: string;
  created_at: string; // ISO date string
  updated_at: string;
  content: string; // markdown body, frontmatter stripped
}

interface Frontmatter {
  data: Record<string, string>;
  content: string;
}

// Minimal frontmatter parser: pulls a leading `---\n...\n---` block of simple
// `key: value` lines. Surrounding quotes are stripped. No nested YAML needed.
// Strip HTML comments — react-markdown renders them as literal text.
function stripComments(md: string): string {
  return md.replace(/<!--[\s\S]*?-->/g, "");
}

function parseFrontmatter(raw: string): Frontmatter {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(raw);
  if (!match) return { data: {}, content: stripComments(raw) };

  const data: Record<string, string> = {};
  for (const line of match[1].split(/\r?\n/)) {
    const idx = line.indexOf(":");
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    let value = line.slice(idx + 1).trim();
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    if (key) data[key] = value;
  }
  return { data, content: stripComments(raw.slice(match[0].length)) };
}

const modules = import.meta.glob<string>("./*.md", {
  query: "?raw",
  import: "default",
  eager: true,
});

const posts: Post[] = Object.entries(modules).map(([path, raw]) => {
  const { data, content } = parseFrontmatter(raw);
  const filename = path.split("/").pop()!.replace(/\.md$/, "");
  return {
    slug: data.slug || filename,
    title: data.title || filename,
    author: data.author || "",
    created_at: data.created_at || "",
    updated_at: data.updated_at || data.created_at || "",
    content,
  };
});

const seen = new Set<string>();
for (const post of posts) {
  if (seen.has(post.slug)) {
    throw new Error(`Duplicate blog slug: "${post.slug}"`);
  }
  seen.add(post.slug);
}

export const POSTS: Post[] = posts.sort((a, b) =>
  b.created_at.localeCompare(a.created_at),
);

export function getPost(slug: string | undefined): Post | undefined {
  return POSTS.find((p) => p.slug === slug);
}
